(() => {
  const SELECTOR_ROOT = '[data-sc-ai-image-label]';
  const SELECTOR_TRIGGER = '[data-sc-ai-image-label-trigger]';
  const SELECTOR_TOOLTIP = '[data-sc-ai-image-label-tooltip]';
  const HOVER_MQ = '(hover: hover) and (pointer: fine)';

  let openRoot = null;

  const canHover = () =>
    typeof window.matchMedia === 'function' && window.matchMedia(HOVER_MQ).matches;

  const closeLabel = (root) => {
    if (!root) return;
    const trigger = root.querySelector(SELECTOR_TRIGGER);
    const tooltip = root.querySelector(SELECTOR_TOOLTIP);
    root.classList.remove('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (tooltip) tooltip.hidden = true;
    if (openRoot === root) openRoot = null;
  };

  const openLabel = (root) => {
    if (!root) return;
    if (openRoot && openRoot !== root) closeLabel(openRoot);
    const trigger = root.querySelector(SELECTOR_TRIGGER);
    const tooltip = root.querySelector(SELECTOR_TOOLTIP);
    root.classList.add('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    if (tooltip) tooltip.hidden = false;
    openRoot = root;
  };

  const toggleLabel = (root) => {
    if (root.classList.contains('is-open')) {
      closeLabel(root);
    } else {
      openLabel(root);
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest(SELECTOR_TRIGGER);
      if (trigger) {
        const root = trigger.closest(SELECTOR_ROOT);
        if (!root) return;
        event.preventDefault();
        event.stopPropagation();
        // Desktop hover devices rely on hover; click still toggles for keyboard/touch hybrids
        toggleLabel(root);
        return;
      }

      if (openRoot && !event.target.closest(SELECTOR_ROOT)) {
        closeLabel(openRoot);
      }
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && openRoot) {
      const trigger = openRoot.querySelector(SELECTOR_TRIGGER);
      closeLabel(openRoot);
      if (trigger) trigger.focus();
    }
  });

  document.addEventListener('focusin', (event) => {
    const root = event.target.closest(SELECTOR_ROOT);
    if (root && event.target.closest(SELECTOR_TRIGGER)) {
      openLabel(root);
      return;
    }
    if (openRoot && (!root || root !== openRoot)) {
      closeLabel(openRoot);
    }
  });

  document.addEventListener(
    'pointerover',
    (event) => {
      if (!canHover()) return;
      const root = event.target.closest?.(SELECTOR_ROOT);
      if (!root || root.classList.contains('is-open')) return;
      openLabel(root);
    },
    true
  );

  document.addEventListener(
    'pointerout',
    (event) => {
      if (!canHover()) return;
      const root = event.target.closest?.(SELECTOR_ROOT);
      if (!root) return;
      const related = event.relatedTarget;
      if (related && root.contains(related)) return;
      closeLabel(root);
    },
    true
  );

  const MARKER_MODIFIED = '$ki-modified';
  const MARKER_GENERATED = '$ki-generated';
  const MARKER_DEFAULT = '$ki';
  const config = window.scAiImageLabelConfig || {};
  let labelSeq = 0;

  const labelType = (alt) => {
    if (alt.includes(MARKER_MODIFIED)) return 'modified';
    if (alt.includes(MARKER_GENERATED)) return 'generated';
    if (alt.includes(MARKER_DEFAULT)) return 'default';
    return null;
  };

  const cleanAlt = (alt) =>
    alt
      .split(MARKER_MODIFIED)
      .join('')
      .split(MARKER_GENERATED)
      .join('')
      .split(MARKER_DEFAULT)
      .join('')
      .replace(/\s+/g, ' ')
      .trim();

  const findHost = (img) =>
    img.closest('picture') ||
    img.closest('.banner__media') ||
    img.closest('.media') ||
    img.closest('.banner') ||
    img.parentElement;

  const buildLabel = (type, id) => {
    const texts = config.texts || {};
    const images = config.images || {};
    const fallback = 'Dieses Bild wurde mithilfe künstlicher Intelligenz erstellt oder bearbeitet.';
    const text = (texts[type] || texts.default || fallback).trim() || fallback;
    const customSrc = type === 'modified' || type === 'generated' ? images[type] : '';
    const src = customSrc || config.icon;
    const position = config.position || 'bottom-left';
    const color = config.color || '#FFFFFF';
    const hasImage = Boolean(customSrc);

    const root = document.createElement('span');
    root.className = `sc-ai-image-label sc-ai-image-label--${position}${hasImage ? ' sc-ai-image-label--custom-image' : ''}`;
    root.style.setProperty('--sc-ai-image-label-color', color);
    root.setAttribute('data-sc-ai-image-label', '');

    const button = document.createElement('button');
    button.type = 'button';
    button.className = `sc-ai-image-label__button${hasImage ? ' sc-ai-image-label__button--custom-image' : ''}`;
    button.setAttribute('aria-label', text);
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-controls', `sc-ai-image-label-tooltip-${id}`);
    button.setAttribute('data-sc-ai-image-label-trigger', '');

    const icon = document.createElement('img');
    icon.className = hasImage ? 'sc-ai-image-label__icon sc-ai-image-label__image' : 'sc-ai-image-label__icon';
    icon.alt = '';
    icon.width = 44;
    icon.height = 44;
    icon.decoding = 'async';
    if (src) icon.src = src;
    button.appendChild(icon);

    const tooltip = document.createElement('span');
    tooltip.id = `sc-ai-image-label-tooltip-${id}`;
    tooltip.className = 'sc-ai-image-label__tooltip';
    tooltip.setAttribute('role', 'tooltip');
    tooltip.hidden = true;
    tooltip.setAttribute('data-sc-ai-image-label-tooltip', '');
    tooltip.textContent = text;

    root.append(button, tooltip);
    return root;
  };

  const textOf = (node) => (node?.textContent || '').replace(/\s+/g, ' ').trim();

  const fallbackAlt = (img) => {
    const card = img.closest('.product-card');
    const cardTitle = textOf(card?.querySelector('.product-card__title'));
    if (cardTitle) return cardTitle;

    const row = img.closest('.horizontal-product, .hotspot');
    const rowTitle = textOf(row?.querySelector('.horizontal-product__title'));
    if (rowTitle) return rowTitle;

    const link = img.closest('a[aria-label]');
    const linkLabel = (link?.getAttribute('aria-label') || '').trim();
    if (linkLabel) return linkLabel;

    const section = img.closest('.shopify-section, section');
    const heading = section?.querySelector('.product__title, .title-wrapper .heading, .banner__box .heading, .rich-text .heading');
    return textOf(heading);
  };

  const mountImage = (img) => {
    if (!(img instanceof HTMLImageElement)) return;
    if (img.dataset.scAiChecked === '1') return;
    if (img.closest(SELECTOR_ROOT)) return;

    const alt = img.getAttribute('alt') || '';
    const explicitType = (img.getAttribute('data-sc-ai-label') || '').trim();
    const type = explicitType || labelType(alt);
    if (!type) {
      img.dataset.scAiChecked = '1';
      return;
    }

    img.dataset.scAiChecked = '1';
    if (labelType(alt)) {
      const cleaned = cleanAlt(alt);
      img.setAttribute('alt', cleaned || fallbackAlt(img));
    }

    if (config.enabled === false) return;
    if (img.closest('.product__thumbnail')) return;

    const host = findHost(img);
    if (!host || host === document.body || host === document.documentElement) return;
    if (host.querySelector(SELECTOR_ROOT)) return;

    if (window.getComputedStyle(host).position === 'static') {
      host.style.position = 'relative';
    }
    host.classList.add('sc-ai-image-label-host');
    host.appendChild(buildLabel(type, `auto-${++labelSeq}`));
  };

  const scan = (root) => {
    const scope = root && root.querySelectorAll ? root : document;
    if (scope instanceof HTMLImageElement) {
      mountImage(scope);
      return;
    }
    scope.querySelectorAll('img').forEach(mountImage);
  };

  scan(document);

  const observer = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        scan(node);
      });
    });
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
